import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createManualPayment } from '@severed-links/common.redherrings-reducers/payment'

const paymentErrorMessage = error => {
    const apiMessage = error?.response?.message
    const detail = typeof apiMessage === 'string' && apiMessage.trim()
        ? apiMessage : typeof error?.message === 'string' ? error.message : ''
    if (Number.isInteger(error?.status)) {
        const fallback = error.status === 404
            ? 'The manual-payment endpoint was not found. Check that the updated API is deployed.'
            : error.statusText || 'The server rejected the payment request.'
        return `Payment request failed (HTTP ${error.status}): ${detail || fallback}`
    }
    return `Unable to confirm payment${detail ? `: ${detail.replace(/[.!?]+$/, '')}.` : '.'} Retry with the same details or refresh to check the log.`
}

// The shared action owns the request; this hook keeps form and retry state.
const useManualPayment = ({ applicationName, seasonId, player, onSaved }) => {
    const dispatch = useDispatch()
    const [expectedBalance] = useState(() => Math.round(player.balance * 100))
    const [amount, setAmount] = useState(() => Number(player.balance).toFixed(2))
    const [paymentMethod, setPaymentMethod] = useState('Cash')
    const [paymentReference, setPaymentReference] = useState('')
    const [paymentNote, setPaymentNote] = useState('')
    const [isSaving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const pending = useRef(false)
    const attempt = useRef(null)
    const validAmount = /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) > 0 &&
        Math.round(Number(amount) * 100) <= expectedBalance
    const isValid = validAmount && (paymentMethod !== 'Other' || !!paymentNote.trim())
    const save = async () => {
        if (pending.current || !isValid) return
        if (typeof createManualPayment !== 'function') {
            setError('Manual payments are unavailable: the installed Redherrings reducers package does not include createManualPayment. Install the updated package and rebuild/restart the app. No payment request was sent.')
            return
        }
        pending.current = true
        setSaving(true)
        setError('')
        const body = { applicationName, seasonId, playerId: player.playerId, amount,
            expectedBalance, paymentMethod, paymentReference, paymentNote }
        const signature = JSON.stringify(body)
        let requestStarted = false
        let recorded = false
        try {
            if (attempt.current?.signature !== signature) attempt.current = { signature, requestId: crypto.randomUUID() }
            const action = createManualPayment({ ...body, requestId: attempt.current.requestId })
            requestStarted = true
            const result = await dispatch(action)
            if (!result || result.error || result.payload?.messageType !== 'success') {
                setError(paymentErrorMessage(result?.payload))
            } else {
                recorded = true
            }
        } catch (error) {
            setError(requestStarted ? paymentErrorMessage(error)
                : `Payment request could not be prepared: ${error?.message || 'unexpected application error'}. No payment request was sent.`)
        }
        pending.current = false
        setSaving(false)
        if (recorded) {
            try {
                await onSaved()
            } catch (error) {
                setError('Payment was recorded, but the payment log could not refresh. Refresh the payments page to see the entry.')
            }
        }
    }
    return { amount, setAmount, paymentMethod, setPaymentMethod, paymentReference, setPaymentReference,
        paymentNote, setPaymentNote, isSaving, error, isValid, save, maxAmount: expectedBalance / 100 }
}

export default useManualPayment
